import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../services/api';
import './Login.css';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Countdown timer effect
  useEffect(() => {
    if (!retryAfter) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const retryTime = new Date(retryAfter);
      const diff = Math.max(0, Math.floor((retryTime - now) / 1000));

      if (diff <= 0) {
        setTimeRemaining(null);
        setRetryAfter(null);
        return;
      }

      setTimeRemaining(diff);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [retryAfter]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if still in cooldown
    if (timeRemaining && timeRemaining > 0) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    setRetryAfter(null);

    try {
      const response = await apiClient.post('/api/v1/auth/forgot-password', {
        email: email
      });

      // Check if rate limited
      if (response.data.rate_limited && response.data.retry_after) {
        setRetryAfter(response.data.retry_after);
        setMessage(response.data.message || 'If this email exists, you\'ll receive a password reset link');
      } else {
        setMessage(response.data.message || 'If this email exists, you\'ll receive a password reset link');
        // Store the time of successful request to show timer on next attempt
        const nextRetryTime = new Date(Date.now() + 60000).toISOString();
        setRetryAfter(nextRetryTime);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Forgot Password</h1>
          <p>Enter your email address and we'll send you a link to reset your password</p>
        </div>

        {message && (
          <div className={`alert ${timeRemaining ? 'alert-warning' : 'alert-success'}`} style={{ marginBottom: '20px' }}>
            {message}
            {timeRemaining && timeRemaining > 0 && (
              <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                Please wait <span style={{ color: '#667eea', fontSize: '16px' }}>{formatTime(timeRemaining)}</span> before requesting another reset link.
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={error ? 'error' : ''}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading || (timeRemaining && timeRemaining > 0)}
            />
          </div>

          <button 
            type="submit" 
            className={`submit-button ${loading ? 'loading' : ''}`}
            disabled={loading || (timeRemaining && timeRemaining > 0)}
          >
            {loading ? 'Sending...' : (timeRemaining && timeRemaining > 0) ? `Wait ${formatTime(timeRemaining)}` : 'Send Reset Link'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login">Sign in here</Link>
          </p>
          <p>
            Don't have an account?{' '}
            <Link to="/register">Sign up here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;

